/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface SupportPhoneNumberProps {
    className?: string;
    hideCopyAtMobile?: boolean;
}

export default function SupportPhoneNumber({
    className,
    hideCopyAtMobile = false,
}: SupportPhoneNumberProps): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <a
            href={t('footerBottom.phoneHref')}
            className={cn(
                'inline-flex items-center gap-2 text-xs leading-4 text-footer-foreground underline-offset-4 transition-colors hover:underline focus-visible:underline',
                className
            )}>
            <Phone aria-hidden="true" className="size-4 shrink-0" />
            <span className={cn(hideCopyAtMobile && 'hidden lg:inline')}>{t('footerBottom.phoneDisplay')}</span>
        </a>
    );
}
